import { memo, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import QuantityInput from "../../common/QuantityInput";
import {
  maxLength,
  minLength,
  minValue,
  number,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormContext, useWatch } from "react-hook-form";
import SubtotalInput from "../../common/SubtotalInput";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import TransportDiscountInput from "../../common/TransportDiscountInput";
import { useMutation } from "react-query";
import SaleOrderItemReference from "../../common/SaleOrderItemReference";
import BonusQuantityInput from "../../common/BonusQuantityInput";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus";
import useSaleOrderIsKeramik from "../../common/useSaleOrderIsKeramik";

// ====== VALIDASI ======
const equalOrLessThan = (value, allValues, elm) => {
  const splite = elm.source.split(".");
  const orderQty = allValues[splite[0]][+splite[1]].quantity;
  if (value > orderQty) {
    return "Must be equal or less than " + orderQty + " (order quantity)";
  }
  return undefined;
};

const greaterThanZero = (value) => {
  if (value === 0) return undefined;
  if (value < 0)
    return { message: "ra.validation.minValue", args: { min: 1 } };
  return undefined;
};

const validateSaleOrderItemId = [required(), minLength(36), maxLength(36)];
const validateDisc = [number(), greaterThanZero];
const validateQuantity = [required(), number(), minValue(1), equalOrLessThan];
const validateBonus = [number(), equalOrLessThan];
const validatePrice = [required(), number(), minValue(1)];

// ====== TRANSPORT & SUBTOTAL ======
const TransportDiscount = ({
  discSource,
  priceSource,
  spcDiscSource,
  subtotalSource,
  sentQuantitySource,
  saleOrderItemIdSource,
  transportDiscountSource,
}) => {
  const dataProvider = useDataProvider();
  const transportID = useWatch({ name: "transport_id" });
  const { data, mutate, isLoading, reset } = useMutation((transID) =>
    dataProvider.getListNoParams("transports/" + transID)
  );
  useEffect(() => {
    if (transportID && transportID.length === 36) mutate(transportID);
    else reset();
  }, [transportID]);
  return (
    <>
      <Grid item xs={12} lg={1.1} sx={{ display: "none" }}>
        <TransportDiscountInput
          className="transport-disc"
          variant="outlined"
          label="mine.label.transport_discount"
          source={transportDiscountSource}
          fullWidth
          readOnly
          defaultValue={0}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">Rp</InputAdornment>
            ),
          }}
          {...parserFormatNumInput}
          dependOnTransportID="transport_id"
          dependOnSaleOrderItemId={saleOrderItemIdSource}
          dependOnQty={sentQuantitySource}
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <SubtotalInput
          className="sub-total"
          variant="outlined"
          source={subtotalSource}
          fullWidth
          readOnly
          defaultValue={0}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">Rp</InputAdornment>
            ),
          }}
          {...parserFormatNumInput}
          dependOnPrice={priceSource}
          dependOnQty={sentQuantitySource}
          dependOnDisc={discSource}
          dependOnSpcDisc={spcDiscSource}
          dependOnTransportDisc={transportDiscountSource}
          helperText={false}
        />
      </Grid>
    </>
  );
};

// ====== BARIS INPUT ITEM ======
const CstInput = ({
  saleOrderItemIdSource,
  sentQuantitySource,
  bonusQuantitySource,
  quantitySource,
  nameSource,
  discSource,
  spcDiscSource,
  priceSource,
  subtotalSource,
  transportDiscountSource,
  formData,
}) => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();
  const isKeramik = useSaleOrderIsKeramik();

  // Ref ke input sent quantity
  const sentQuantityRef = useRef(null);

  // Enter -> tambah baris baru + fokus ke field saleOrderItemId berikutnya
  useEnterToAddRowAndFocus(sentQuantityRef, undefined, 'input[name*="sale_order_item_id"]');

  const onSaleOrderItemIdChangeHandler = async (uuid) => {
    setValue(sentQuantitySource, "");
    setValue(bonusQuantitySource, "");
    if (uuid.length !== 36) {
      setValue(nameSource, "");
      setValue(quantitySource, "");
      setValue(priceSource, "");
    }
    if (uuid) {
      try {
        const { product, quantity, price } = await dataProvider.getListNoParams(
          "sale-order-items/" + encodeURIComponent(uuid)
        );
        setValue(nameSource, product.name);
        setValue(quantitySource, quantity);
        setValue(priceSource, price);
      } catch (error) { }
    }
  };

  return (
    <Grid container spacing={2} sx={{ "& .MuiGrid-item": { py: 0 }, mt: 0 }}>
      {/* ================= BARIS ATAS: KODE + NAMA ================= */}
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SaleOrderItemReference
            variant="outlined"
            source={saleOrderItemIdSource}
            fullWidth
            label="mine.label.product_code"
            validate={validateSaleOrderItemId}
            dependOn="sale_order_id"
            queryName="sale_order_id"
            reference="sale-order-items"
            onChange={onSaleOrderItemIdChangeHandler}
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <TextInput
            variant="outlined"
            source={nameSource}
            label="mine.label.name"
          readOnly
            fullWidth
            helperText={false}
          />
        </Grid>
      </Grid>

      {/* ================= BARIS BAWAH: FIELD LAIN ================= */}
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={3} md={1.5}>
          <TextInput
            variant="outlined"
            source={quantitySource}
            label="mine.label.order"
          readOnly
            fullWidth
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={1.5}>
          <BonusQuantityInput
            variant="outlined"
            source={bonusQuantitySource}
            label="Bonus"
            fullWidth
            validate={validateBonus}
            helperText={false}
            dependOn="bonus_sale_items"
            saleOrderItemIdSource={saleOrderItemIdSource}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <TextInput
            variant="outlined"
            source={priceSource}
            validate={validatePrice}
            readOnly={!isKeramik}
            fullWidth
            label="mine.label.price_per_box"
            InputProps={{
              startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
            }}
            {...parserFormatNumInput}
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <TextInput
            variant="outlined"
            source={discSource}
            validate={validateDisc}
            fullWidth
            label="mine.label.discount_per_box"
            InputProps={{
              startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
            }}
            {...parserFormatNumInput}
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <TextInput
            variant="outlined"
            source={spcDiscSource}
            validate={validateDisc}
            fullWidth
            label="mine.label.special_discount_per_box"
            InputProps={{
              startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
            }}
            {...parserFormatNumInput}
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={1.5} ref={sentQuantityRef}>
          <QuantityInput
            variant="outlined"
            source={sentQuantitySource}
            label="mine.label.sent"
            fullWidth
            validate={validateQuantity}
            helperText={false}
          />
        </Grid>

        {/* Transport & Subtotal */}
        <TransportDiscount
          {...{
            discSource,
            priceSource,
            spcDiscSource,
            subtotalSource,
            sentQuantitySource,
            saleOrderItemIdSource,
            transportDiscountSource,
          }}
        />
      </Grid>
    </Grid>
  );
};

// ====== OPTIMASI MEMO ======
const propsAreEqual = (prev, next) => {
  if (
    prev.formData.transport_id === next.formData.transport_id &&
    prev.formData.sale_order_id === next.formData.sale_order_id &&
    prev.sale_order_item_id === next.sale_order_item_id &&
    prev.sent_quantity === next.sent_quantity &&
    prev.bonus_quantity === next.bonus_quantity &&
    prev.disc === next.disc &&
    prev.spc_disc === next.spc_disc
  ) {
    return true;
  }
  return false;
};
const Beruk = memo(CstInput, propsAreEqual);

// ====== WRAPPER UNTUK ARRAYINPUT ======
const Inputs = ({ formData, scopedFormData, getSource }) => (
  <Beruk
    {...scopedFormData}
    formData={formData}
    saleOrderItemIdSource={getSource("sale_order_item_id")}
    sentQuantitySource={getSource("sent_quantity")}
    bonusQuantitySource={getSource("bonus_quantity")}
    quantitySource={getSource("quantity")}
    priceSource={getSource("price")}
    nameSource={getSource("name")}
    discSource={getSource("disc")}
    spcDiscSource={getSource("spc_disc")}
    subtotalSource={getSource("subtotal")}
    transportDiscountSource={getSource("transport_discount")}
  />
);

export default Inputs;
